import { AxiosError, AxiosResponse } from 'axios';
import { FieldTextarea } from 'components/redesign/FieldTextArea';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { checkName } from 'pages/ModerationRequest/ExecutorModeration/steps/step1/consts';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { MultiValue, SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getInvitationPageFilters } from 'reduxStore/reducers/OrdersSlice/selectors/getInvitationPageFilters';
import { InvitationService } from 'services/InvitationService';
import {
  InvitationCooperate,
  InvitationCooperateRequestDto,
} from 'services/InvitationService/types';
import { BaseError } from 'types';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { OptionType } from 'ui/redesign/Select/types';
import { Spinner } from 'ui/Spinner';
import { alertBaseError, prepareFilterData } from 'utils/helpers';
import { checkClientInviteEmail } from './consts';
import { ClientInviteModalForm, ClientInviteModalProps } from './types';

export const ClientInviteModal = ({ refetch }: ClientInviteModalProps) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector(getInvitationPageFilters);

  const {
    control,
    watch,
    setError,
    formState: { errors },
    clearErrors,
    handleSubmit,
  } = useForm<ClientInviteModalForm>({
    defaultValues: {
      firstName: null,
      lastName: null,
      middleName: null,
      email: null,
    },
  });

  const { mutateAsync: sendInvite, isLoading } = useMutation<
    AxiosResponse<InvitationCooperate>,
    AxiosError<BaseError>,
    InvitationCooperateRequestDto
  >({
    mutationKey: 'sendInvite',
    mutationFn: ({ firstName, lastName, middleName, email }) => {
      return InvitationService.sendInvite({
        firstName,
        lastName,
        middleName,
        email,
      });
    },
    onSuccess: () => {
      toast.success('Приглашение создано');
      cancelHandler();
      const statuses = filters.statuses
        ? prepareFilterData(
            filters.statuses as unknown as MultiValue<OptionType> | SingleValue<OptionType>
          )
        : null;
      refetch({
        createDate: filters.createDate,
        fullName: filters.fullName,
        email: filters.email,
        statuses,
      });
    },
    onError: (err: AxiosError<BaseError>) => {
      alertBaseError(err);
    },
  });

  const cancelHandler = () => {
    dispatch(modalActions.closeModal());
  };

  const onSubmit = () => {
    handleSubmit((data) => {
      const trimmedData = {
        ...data,
        firstName: data.firstName ? data.firstName.trim() : data.firstName,
        lastName: data.lastName ? data.lastName.trim() : data.lastName,
        middleName: data.middleName ? data.middleName.trim() : data.middleName,
      };
      const toastErrors: string[] = [];
      checkName(trimmedData.firstName, 'firstName', 'Имя должно', toastErrors, setError);
      checkName(trimmedData.lastName, 'lastName', 'Фамилия должна', toastErrors, setError);
      if (trimmedData.middleName) {
        checkName(trimmedData.middleName, 'middleName', 'Отчество должно', toastErrors, setError);
      }

      checkClientInviteEmail(data.email, 256, toastErrors, setError);

      if (toastErrors.length) {
        const onlyUnique = new Set(toastErrors);
        Array.from(onlyUnique).forEach((message: string) => {
          toast.error(message);
        });
        return;
      }
      sendInvite({ ...trimmedData, email: data.email });
    })();
  };

  const isButtonDisabled = !watch('lastName') || !watch('firstName') || !watch('email');

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <form className="mt-6 flex flex-col gap-6">
          <FieldTextarea
            name={`lastName`}
            control={control}
            label="Фамилия"
            errorMessage={errors?.lastName?.message}
            isClearable
            maxLength={200}
            clearErrors={clearErrors}
          />
          <FieldTextarea
            name={`firstName`}
            control={control}
            label="Имя"
            errorMessage={errors?.firstName?.message}
            isClearable
            maxLength={200}
            clearErrors={clearErrors}
          />
          <FieldTextarea
            name={`middleName`}
            control={control}
            label="Отчество"
            errorMessage={errors?.middleName?.message}
            isClearable
            maxLength={200}
            clearErrors={clearErrors}
          />
          <FieldTextarea
            name="email"
            control={control}
            label="E-mail"
            errorMessage={errors?.email?.message}
            maxLength={256}
            isClearable
            clearErrors={clearErrors}
          />
          <div className="mt-6 flex flex-col-reverse gap-6 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant={ButtonVariant.GHOST}
              onClick={cancelHandler}
              className="paragraphs-bold mb-1 w-full max-w-full border border-solid border-text-50 !text-text-100 sm:w-[130px]"
            >
              ОТМЕНА
            </Button>
            <Button
              type="button"
              className="paragraphs-bold mb-1 w-full max-w-full sm:w-[190px]"
              onClick={onSubmit}
              disabled={isButtonDisabled}
            >
              СОЗДАТЬ
            </Button>
          </div>
        </form>
      )}
    </>
  );
};
